import { useEffect, useState } from 'react';
import { WifiOff, CloudOff, RefreshCw, Trash2, X } from 'lucide-react';
import { subscribeOnlineStatus, manualFlush } from '../lib/axiosOffline';
import { clear as clearQueue, getAll as getQueueItems } from '../lib/offlineQueue';
import { toast } from 'sonner';

export const OfflineIndicator = () => {
    const [state, setState] = useState({ online: true, pending: 0 });
    const [busy, setBusy] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [items, setItems] = useState([]);

    useEffect(() => {
        const unsub = subscribeOnlineStatus(setState);
        return unsub;
    }, []);

    useEffect(() => {
        if (showDetails) {
            getQueueItems().then(setItems).catch(() => setItems([]));
        }
    }, [showDetails, state.pending]);

    const handleSync = async () => {
        if (busy) return;
        setBusy(true);
        await manualFlush();
        setBusy(false);
    };

    const handleDiscard = async () => {
        if (!window.confirm('Esto borrara todas las acciones pendientes en la cola. ¿Estas seguro?')) return;
        await clearQueue();
        toast.success('Cola vaciada');
        setShowDetails(false);
        // forzar update del state
        setState((s) => ({ ...s, pending: 0 }));
    };

    if (state.online && state.pending === 0) return null;

    return (
        <>
            <button
                type="button"
                onClick={() => state.online ? handleSync() : setShowDetails(true)}
                onContextMenu={(e) => { e.preventDefault(); setShowDetails(true); }}
                className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 px-4 py-2 rounded-full shadow-2xl animate-fadeIn cursor-pointer hover:scale-105 transition-transform"
                style={{
                    background: state.online ? 'rgba(245, 158, 11, 0.95)' : 'rgba(220, 38, 38, 0.95)',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    backdropFilter: 'blur(10px)'
                }}
                data-testid="offline-indicator"
                title={state.online ? 'Click para sincronizar ahora · Click derecho para detalles' : 'Click para ver acciones pendientes'}
            >
                {state.online ? (
                    <>
                        <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : 'animate-pulse'}`} />
                        <span>{busy ? 'Sincronizando...' : `Sincronizar ${state.pending} accion${state.pending !== 1 ? 'es' : ''}`}</span>
                    </>
                ) : (
                    <>
                        <WifiOff className="w-4 h-4" />
                        <span>Sin conexion. Tus cambios se guardan localmente.</span>
                        {state.pending > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-white/20 text-[10px]">{state.pending} pendiente{state.pending !== 1 ? 's' : ''}</span>
                        )}
                    </>
                )}
            </button>

            {showDetails && (
                <div
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110] flex items-end sm:items-center justify-center p-4 animate-fadeIn"
                    onClick={() => setShowDetails(false)}
                >
                    <div
                        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="offline-details-modal"
                    >
                        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
                            <h2 className="text-base font-bold text-zinc-100 flex items-center gap-2">
                                <CloudOff className="w-4 h-4 text-amber-400" />
                                Acciones pendientes ({state.pending})
                            </h2>
                            <button onClick={() => setShowDetails(false)} className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-400">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4 space-y-2 overflow-y-auto max-h-[50vh]">
                            {items.length === 0 ? (
                                <p className="text-zinc-500 text-xs text-center py-6">No hay acciones en cola</p>
                            ) : items.map((it) => (
                                <div key={it.id} className="p-2 rounded-lg bg-zinc-800/60 border border-zinc-700 text-xs">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                            it.method === 'POST' ? 'bg-emerald-500/20 text-emerald-400' :
                                            it.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                                            'bg-blue-500/20 text-blue-400'
                                        }`}>{it.method}</span>
                                        <span className="text-zinc-300 truncate flex-1">{(it.url || '').split('/api/')[1] || it.url}</span>
                                        {it.retries > 0 && (
                                            <span className="text-[10px] text-amber-400">{it.retries} reintento{it.retries !== 1 ? 's' : ''}</span>
                                        )}
                                    </div>
                                    {it.ts && <p className="text-[10px] text-zinc-500 mt-1">{new Date(it.ts).toLocaleString()}</p>}
                                </div>
                            ))}
                        </div>
                        <div className="p-4 border-t border-zinc-700 flex gap-2">
                            {state.online && state.pending > 0 && (
                                <button
                                    onClick={async () => { await handleSync(); }}
                                    disabled={busy}
                                    className="flex-1 px-3 py-2 rounded-lg bg-amber-400 text-zinc-900 font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                                    data-testid="btn-sync-now"
                                >
                                    <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
                                    {busy ? 'Sincronizando...' : 'Sincronizar ahora'}
                                </button>
                            )}
                            {state.pending > 0 && (
                                <button
                                    onClick={handleDiscard}
                                    className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-red-500/20 hover:text-red-400 text-zinc-400 text-sm flex items-center gap-2"
                                    data-testid="btn-discard-queue"
                                    title="Descartar todas las acciones pendientes"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Descartar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
